import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileSpreadsheet, FileText, FolderArchive, ShoppingCart, Table2, Clock, Loader2, X } from "lucide-react";
import { ClientCodeInput } from "@/components/ClientCodeInput";
import { RawApiProcure } from "@/components/RawApiProcure";

const GROUPS = [
  { k: "full", label: "Full list" },
  { k: "service", label: "Service + country" },
  { k: "client", label: "Client-wise" },
];

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
      active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
    {children}
  </button>
);

// Iter 281 — Pending Center: filters + every pending output in one window.
export const PendingCenter = ({ open, onClose, isAdmin, onShowInTable }) => {
  const [f, setF] = useState({ date_from: "", date_to: "", age7: false, client_code: "", service: "", user_id: "", reason_scope: "with" });
  const [group, setGroup] = useState("service");
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState("");

  const params = useCallback(() => Object.fromEntries(
    Object.entries(f).filter(([, v]) => v !== "" && v !== false)), [f]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/order-register/pending-center/summary`,
          { headers: getAuthHeader(), params: params() });
        setSum(r.data);
      } catch { setSum(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [open, params]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const clearFilters = () => setF({ date_from: "", date_to: "", age7: false, client_code: "", service: "", user_id: "", reason_scope: "with" });
  const hasFilters = Object.entries(f).some(([k, v]) => (k === "reason_scope" ? v !== "with" : v !== "" && v !== false));

  const download = async (path, extra, key) => {
    setBusy(key);
    try {
      const r = await axios.get(`${API}/order-register/pending-center/${path}`,
        { headers: getAuthHeader(), params: { ...params(), ...extra }, responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "pending"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const st = e.response?.status;
      toast.error(st === 403 ? "Not allowed for your service scope" : `Download failed${st ? ` (${st})` : ""}`);
    }
    setBusy("");
  };

  const B = ({ k, icon: Icon, children, onClick, tone = "slate" }) => (
    <Button size="sm" variant="outline" onClick={onClick} disabled={!!busy}
      className={`justify-start ${tone === "emerald" ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        : tone === "violet" ? "border-violet-300 text-violet-700 hover:bg-violet-50"
        : tone === "red" ? "border-red-300 text-red-700 hover:bg-red-50" : ""}`}
      data-testid={`pc-${k}`}>
      {busy === k ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Icon className="w-4 h-4 mr-1" />}
      {children}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pending-center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Pending Center
            {sum && (
              <span className="text-sm font-normal text-slate-500" data-testid="pc-total">
                <b className="text-slate-900">{sum.parcels}</b> parcels · {sum.rows} rows
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {f.reason_scope === "with_summary" ? "Downloads: full With-reason list, then a one-line-per-parcel summary of parcels WITHOUT a reason (no product column)" : f.reason_scope === "without" ? "Showing parcels WITHOUT a reason" : f.reason_scope === "all" ? "Showing ALL parcels (with and without reason)" : "Pending = parcels with a reason"}, not cancelled, not on a completed invoice. Filters apply to every download below.
            {sum && sum.rows > 0 && sum.age7_rows === sum.rows && " All current pending rows are already 7+ days old."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 text-xs" data-testid="pc-reason-scope">
          <span className="text-slate-500 mr-1">Parcels:</span>
          {[["with", "With reason"], ["without", "Without reason"], ["all", "All (both)"], ["with_summary", "With reason + No-reason summary"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("reason_scope", v)} data-testid={`pc-reason-${v}`}
              className={`h-7 px-3 rounded-full border font-semibold transition-colors ${
                f.reason_scope === v ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-800 border-amber-300 hover:bg-amber-50"}`}>
              {l}
            </button>
          ))}
        </div>

        <section className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <label className="text-xs text-slate-500">From (order date)
            <Input type="date" value={f.date_from} onChange={e => set("date_from", e.target.value)} className="h-8" data-testid="pc-date-from" />
          </label>
          <label className="text-xs text-slate-500">To
            <Input type="date" value={f.date_to} onChange={e => set("date_to", e.target.value)} className="h-8" data-testid="pc-date-to" />
          </label>
          <div className="text-xs text-slate-500">Client
            <ClientCodeInput value={f.client_code} onChange={v => set("client_code", v)} className="h-8" testid="pc-client" />
          </div>
          <button type="button" onClick={() => set("age7", !f.age7)} data-testid="pc-age7"
            className={`h-8 rounded border text-xs font-semibold inline-flex items-center justify-center gap-1 transition-colors ${
              f.age7 ? "bg-red-600 text-white border-red-600" : "bg-white text-red-600 border-red-300 hover:bg-red-50"}`}>
            <Clock className="w-3.5 h-3.5" /> Above 7 days {sum ? `(${sum.age7_rows})` : ""}
          </button>
          <button type="button" onClick={() => set("reason_scope", "without")} data-testid="pc-no-reason-3d"
            title="Parcels with no reason whose order date is 3+ days old — click to view parcels without reason"
            className="h-8 rounded border text-xs font-semibold inline-flex items-center justify-center gap-1 transition-colors bg-amber-50 text-amber-800 border-amber-400 hover:bg-amber-100">
            <Clock className="w-3.5 h-3.5" /> No reason 3+ days ({sum?.no_reason_3d_parcels ?? 0})
          </button>
          <button type="button" onClick={clearFilters} disabled={!hasFilters} data-testid="pc-clear-filters"
            className="h-8 rounded border text-xs font-semibold inline-flex items-center justify-center gap-1 transition-colors border-slate-300 text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
            <X className="w-3.5 h-3.5" /> Clear filters
          </button>
        </section>

        {sum?.by_service_country?.length > 0 && (
          <section className="mt-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Service + country</div>
            <div className="flex flex-wrap gap-1.5" data-testid="pc-services">
              <Chip active={!f.service} onClick={() => set("service", "")} testid="pc-svc-all">All</Chip>
              {sum.by_service_country.map(s => (
                <Chip key={s.key} active={f.service === s.key} onClick={() => set("service", f.service === s.key ? "" : s.key)}
                      testid={`pc-svc-${s.key.replace(/\s+/g, "-")}`}>
                  {s.key} <span className="opacity-60">{s.parcels}</span>
                </Chip>
              ))}
            </div>
          </section>
        )}

        {isAdmin && sum?.users?.length > 0 && (
          <section className="mt-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Processing user (their service grants)</div>
            <div className="flex flex-wrap gap-1.5" data-testid="pc-users">
              <Chip active={!f.user_id} onClick={() => set("user_id", "")} testid="pc-user-all">Everyone</Chip>
              {sum.users.map(u => (
                <Chip key={u.id} active={f.user_id === u.id} onClick={() => set("user_id", f.user_id === u.id ? "" : u.id)}
                      testid={`pc-user-${u.id}`}>
                  {u.name} <span className="opacity-60">{(u.services || []).join(", ")}</span>
                </Chip>
              ))}
            </div>
          </section>
        )}

        <section className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border p-3 space-y-2">
            <div className="text-xs font-semibold text-slate-700">Pending list</div>
            <div className="flex flex-wrap gap-1.5">
              {GROUPS.map(g => <Chip key={g.k} active={group === g.k} onClick={() => setGroup(g.k)} testid={`pc-group-${g.k}`}>{g.label}</Chip>)}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <B k="excel" icon={FileSpreadsheet} tone="emerald" onClick={() => download("export", { fmt: "excel", group }, "excel")}>Excel</B>
              <B k="pdf" icon={FileText} tone="red" onClick={() => download("export", { fmt: "pdf", group }, "pdf")}>PDF</B>
            </div>
          </div>
          <div className="rounded-lg border p-3 space-y-2">
            <div className="text-xs font-semibold text-slate-700">Client ZIP</div>
            <p className="text-[11px] text-slate-500">One file per client code: pending on top, <b>today's dispatched</b> (invoice completed today) below.</p>
            <div className="grid grid-cols-2 gap-2">
              <B k="zip" icon={FolderArchive} tone="emerald" onClick={() => download("export", { fmt: "zip" }, "zip")}>ZIP (Excel)</B>
              <B k="zip-pdf" icon={FolderArchive} tone="red" onClick={() => download("export", { fmt: "zip_pdf" }, "zip-pdf")}>ZIP (PDF)</B>
            </div>
          </div>
          <div className="rounded-lg border p-3 space-y-2">
            <div className="text-xs font-semibold text-slate-700">Procure list</div>
            <p className="text-[11px] text-slate-500">Products to arrange: code · medicine · second name · total qty.</p>
            <div className="grid grid-cols-2 gap-2">
              <B k="procure-excel" icon={ShoppingCart} tone="violet" onClick={() => download("procure", { fmt: "excel" }, "procure-excel")}>Excel</B>
              <B k="procure-pdf" icon={FileText} tone="violet" onClick={() => download("procure", { fmt: "pdf" }, "procure-pdf")}>PDF</B>
              <div className="col-span-2">
                <B k="shriram-excel" icon={ShoppingCart} tone="violet" onClick={() => download("procure", { fmt: "shriram" }, "shriram-excel")}>Shriram Orders (Excel)</B>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">Shriram Orders: Order Date · Product Name · Qty1 only. Raw / API products are excluded.</p>
          </div>
          {isAdmin && <RawApiProcure params={params} onDownload={download} busy={busy} />}
        </section>

        {sum?.by_client?.length > 0 && (
          <section className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Clients with pending</div>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="pc-clients">
              {sum.by_client.map(c => (
                <Chip key={c.key} active={f.client_code === c.key} onClick={() => set("client_code", f.client_code === c.key ? "" : c.key)}
                      testid={`pc-client-${c.key}`}>
                  {c.key} <span className="opacity-60">{c.parcels}</span>
                </Chip>
              ))}
            </div>
          </section>
        )}

        <footer className="mt-4 flex justify-between">
          <Button variant="ghost" size="sm" onClick={clearFilters}
                  data-testid="pc-reset"><X className="w-4 h-4 mr-1" /> Reset filters</Button>
          <Button size="sm" onClick={() => { onShowInTable(f); onClose(); }} data-testid="pc-show-in-table">
            <Table2 className="w-4 h-4 mr-1" /> Show in table
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
